import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getAllReports,
  getReportTypes,
  getSharedDrivesByIdsQuery,
  getAllResourceCalendars,
  UserKeys,
} from '../../../api/users-api';
import { ReportModel, ReportType, SharedDriveModel } from '../../../../types';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { gPanelTiers } from '../../../shared/gPanel-tiers';
import { Messages } from '../../../utils/message-constants';
import { useReportActions } from '../../../shared/hooks/use-report-actions';
import AvatarChip from '../../../shared/avatar-chip';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../../shared/simple-drawer-new';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../../shared/shared-review-layout-new';
import {
  ReviewSectionItem,
  TargetType,
} from '../../../shared/shared-review-layout-item';
import ReportsDeleteDialog from './reports-delete-dialog';
import ReportsRunDialog from './reports-run-dialog';
import dayjs from 'dayjs';
import SharedReviewLayoutMainCard, {
  MainSectionItem,
} from '../../../shared/shared-review-layout-main-card';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import PlayArrow from '@mui/icons-material/PlayArrow';
import UpdateOutlined from '@mui/icons-material/UpdateOutlined';

// Display component

interface DrawerContentProps {
  selectedReport: ReportModel;
  reportTypes: ReportType[] | undefined;
  selectedSharedDrives?: SharedDriveModel[];
  calendarEmailToNameMap?: Map<string, string>;
}

function DrawerContent({
  selectedReport,
  reportTypes,
  selectedSharedDrives,
  calendarEmailToNameMap,
}: Readonly<DrawerContentProps>) {
  const theme = useTheme();
  const [showAllColumns, setShowAllColumns] = useState(false);
  const MAX_CHIPS = Math.min(
    8,
    Math.max(4, Math.ceil((selectedReport.columns?.length ?? 0) / 2))
  );

  const reportTypeFind = reportTypes?.find(
    (reportType) => reportType.name === selectedReport.reportType
  );

  const leftSections: ReviewSection[] = useMemo(() => {
    const sections: ReviewSection[] = [];

    // Basic Information Section
    const basicItems: ReviewSectionItem[] = [
      {
        label: 'Created By:',
        value: selectedReport.createdBy ? (
          <AvatarChip email={selectedReport.createdBy} type="User" />
        ) : (
          'N/A'
        ),
      },
      {
        label: 'Report Type:',
        value: reportTypeFind?.niceName || selectedReport.reportType || 'N/A',
      },
      {
        label: 'Generated File Name:',
        value: selectedReport.generatedFileName || 'N/A',
      },
      {
        label: 'Manual Run:',
        value: !!selectedReport.manualRun,
      },
      {
        label: 'Scheduled Run:',
        value: !!selectedReport.scheduled,
      },
    ];

    if (selectedReport.destFolderName) {
      basicItems.push({
        label: 'Destination Folder:',
        value: selectedReport.destFolderName,
      });
    }

    if (reportTypeFind?.hasDateRange) {
      const reportWithDateRange = selectedReport as ReportModel & {
        dateRange?: [number, number];
      };
      const dateRangeVal = reportWithDateRange?.dateRange
        ? `${dayjs(reportWithDateRange.dateRange[0]).format(
            'MM/DD/YYYY'
          )} - ${dayjs(reportWithDateRange.dateRange[1]).format('MM/DD/YYYY')}`
        : selectedReport.startDate && selectedReport.endDate
        ? `${dayjs(selectedReport.startDate).format('MM/DD/YYYY')} - ${dayjs(
            selectedReport.endDate
          ).format('MM/DD/YYYY')}`
        : '-';
      basicItems.push({
        label: 'Date Range:',
        value: dateRangeVal,
      });
    }

    if (reportTypeFind?.hasDateSelection) {
      const dateSelVal = selectedReport.startDate
        ? dayjs(selectedReport.startDate).format('MM/DD/YYYY')
        : dayjs().subtract(3, 'day').format('MM/DD/YYYY');
      basicItems.push({
        label: 'Date Selection:',
        value: dateSelVal,
      });
    }

    if (selectedReport.columns?.length) {
      const columnChips = (
        <Stack gap={1}>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {selectedReport.columns
              .slice(
                0,
                showAllColumns ? selectedReport.columns.length : MAX_CHIPS
              )
              .map(
                (
                  col: import('../../../../types').SpreadSheetColumn | string
                ) => {
                  const colName =
                    typeof col === 'string' ? col : col.columnName || col.name;
                  return (
                    <AvatarChip key={colName} email={colName} type="Unknown" />
                  );
                }
              )}
          </Stack>
          {selectedReport.columns.length > MAX_CHIPS && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'flex-start',
              }}
            >
              <Button
                size="small"
                onClick={() => setShowAllColumns(!showAllColumns)}
                sx={{ color: theme.palette.semantic.hyperlink.active }}
              >
                {showAllColumns
                  ? 'Show less'
                  : `+${selectedReport.columns.length - MAX_CHIPS} more`}
              </Button>
            </Box>
          )}
        </Stack>
      );
      basicItems.push({
        label: 'Columns:',
        value: columnChips,
        flex: true,
      });
    }

    if (selectedReport.emailsToNotify?.length) {
      basicItems.push({
        label: 'Email Report To:',
        value: selectedReport.emailsToNotify.map((email) => ({
          email,
          type: 'User' as const,
        })),
        variant: 'chip',
      });
    }

    if (selectedReport.permissions?.length) {
      basicItems.push({
        label: 'Share Access To:',
        value: selectedReport.permissions.map((p) => ({
          email: p.userEmail || 'N/A',
          type: 'User' as const,
        })),
        variant: 'chip',
      });
    }

    sections.push({
      title: 'Basic Information',
      leftWidth: {
        labelWidth: 5,
        valueWidth: 7,
      },
      leftItems: basicItems,
    });

    // Sources Section
    const sourceType = selectedReport.sourceType;
    const allSource = selectedReport.allSource;
    const sources = selectedReport.sources || [];

    if (sourceType || sources.length || allSource) {
      const sourcesItems: ReviewSectionItem[] = [];

      const formatSourceType = (type: string | null): string => {
        switch (type) {
          case 'Resource_Calendar':
            return 'Resource Calendar';
          case 'Shared_Drive':
            return 'Shared Drive';
          case null:
            return 'N/A';
          default:
            return type;
        }
      };

      if (
        sourceType &&
        [
          'User',
          'Group',
          'OrgUnit',
          'Domain',
          'Resource_Calendar',
          'Shared_Drive',
        ].includes(sourceType)
      ) {
        sourcesItems.push({
          label: 'Source Type:',
          value: formatSourceType(sourceType),
        });
      }

      if (allSource || sources.length > 0) {
        if (allSource) {
          sourcesItems.push({
            label: 'Sources:',
            value: 'All',
          });
        } else {
          const typeMap = {
            User: 'User',
            Group: 'Group',
            gPanel_Role: 'gPanel Role',
            Resource_Calendar: 'Resource Calendar',
            OrgUnit: 'OrgUnit',
            Shared_Drive: 'Shared Drive',
            Domain: 'Domain',
          };

          const chipType = (typeMap[sourceType as keyof typeof typeMap] ||
            'Unknown') as TargetType;

          const sourcesChips = (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {sources.map((s) => {
                let label = s;
                if (sourceType === 'Shared_Drive' && selectedSharedDrives) {
                  label =
                    selectedSharedDrives.find((drive) => drive.id === s)
                      ?.name ?? s;
                } else if (
                  sourceType === 'Resource_Calendar' &&
                  calendarEmailToNameMap
                ) {
                  label = calendarEmailToNameMap.get(s) || s;
                }
                return <AvatarChip key={s} email={label} type={chipType} />;
              })}
            </Stack>
          );

          sourcesItems.push({
            label: 'Sources:',
            value: sourcesChips,
          });
        }
      }

      sections.push({
        title: 'Sources',
        leftWidth: {
          labelWidth: 5,
          valueWidth: 7,
        },
        leftItems: sourcesItems,
      });
    }

    // Schedule Section
    if (selectedReport.scheduled) {
      const times = selectedReport.times || [];
      const dates = selectedReport.dates || [];

      const scheduleItems: ReviewSectionItem[] = [
        {
          label: 'Times:',
          value: times
            .map((time) => {
              const t = Number.parseInt(time);
              if (t === 0) return '12 AM';
              if (t < 12) return `${t} AM`;
              if (t === 12) return '12 PM';
              return `${t - 12} PM`;
            })
            .join(', '),
        },
        {
          label: 'Dates:',
          value: dates.join(', '),
        },
      ];

      if (
        selectedReport.scheduleEndDate &&
        selectedReport.scheduleEndDate !== 0
      ) {
        scheduleItems.push({
          label: 'Expiration Date:',
          value: dayjs(selectedReport.scheduleEndDate).format('MM/DD/YYYY'),
        });
      }

      sections.push({
        title: 'Schedule',
        leftWidth: {
          labelWidth: 5,
          valueWidth: 7,
        },
        leftItems: scheduleItems,
      });
    }

    return sections;
  }, [
    selectedReport,
    reportTypeFind,
    showAllColumns,
    selectedSharedDrives,
    calendarEmailToNameMap,
  ]);

  const mainSection: MainSectionItem = {
    mainTitle: selectedReport.reportName || 'N/A',
    description: selectedReport.description || 'No description provided.',
    fields: [
      {
        label: 'Last Run Date',
        value: selectedReport.lastRunDate
          ? dayjs(selectedReport.lastRunDate).format('MMM D, YYYY, h:mm A')
          : 'Never Ran',
        icon: UpdateOutlined,
      },
      {
        label: 'Created Date',
        value: selectedReport.createdDate
          ? dayjs(selectedReport.createdDate).format('MMM D, YYYY, h:mm A')
          : 'N/A',
        icon: Check,
      },
    ],
  };

  return (
    <Stack spacing={2}>
      <SharedReviewLayoutMainCard section={mainSection} isDrawer={true} />
      <SharedReviewLayoutNew leftSections={leftSections} isDrawer={true} />
    </Stack>
  );
}

/**
 * Global, self-contained report detail drawer.
 * Driven entirely by the `?selectedReport=<id>` URL search param.
 * Place this once at the App level — no props required.
 */
export default function ReportDrawerDetails() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toolsRoles, changeLogRoles, usersTier } = useUserSettings();

  const selectedReportId = searchParams.get('selectedReport');

  const { data: reports, isLoading: reportsLoading } = useQuery({
    queryKey: [UserKeys.Reports],
    queryFn: getAllReports,
    enabled: !!selectedReportId,
  });

  const { data: reportTypes, isLoading: reportTypesLoading } = useQuery({
    queryKey: [UserKeys.ReportTypes],
    queryFn: getReportTypes,
    enabled: !!selectedReportId,
  });

  const { data: resourceCalendars } = useQuery({
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: () => getAllResourceCalendars(),
    enabled: !!(
      reports?.find((r) => r.id.toString() === selectedReportId)?.sourceType ===
      'Resource_Calendar'
    ),
  });

  const calendarEmailToNameMap = useMemo(() => {
    if (!resourceCalendars) {
      return new Map<string, string>();
    }
    return new Map(
      resourceCalendars.map((c) => [c.resourceEmail, c.resourceName])
    );
  }, [resourceCalendars]);

  const removedHiddenReports =
    reportTypes?.filter((rt) => rt.category !== 'hidden') ?? [];
  const selectedReport =
    reports?.find((r) => r.id.toString() === selectedReportId) ?? null;
  const selectedReportType = removedHiddenReports.find(
    (t) => t.name === selectedReport?.reportType
  );

  const isLoading =
    (reportsLoading || reportTypesLoading) && !!selectedReportId;

  // Fetch shared drives only for the currently viewed report.
  const sharedDriveSources =
    selectedReport?.sourceType === 'Shared_Drive'
      ? selectedReport.sources ?? []
      : [];
  const { data: selectedSharedDrives } = useQuery({
    ...getSharedDrivesByIdsQuery(sharedDriveSources),
    enabled: sharedDriveSources.length > 0,
  });

  // close drawer
  const handleClose = () => {
    setSearchParams((prev) => {
      prev.delete('selectedReport');
      return prev;
    });
  };

  const {
    handleStartReport,
    handleConfirmRunDialog,
    handleConfirmRun,
    isRunning,
    reportForRun,
    openRunConfirmDialog,
    closeRunConfirmDialog,
    changeLogMessage,
    setChangeLogMessage,
    openChangeLogDialog,
    closeChangeLogDialog,
    handleEditReport,
    openDeleteDialog,
    setOpenDeleteDialog,
  } = useReportActions({
    toolsRoles,
    changeLogRoles,
    removedHiddenReports,
    onRunSettled: handleClose,
  });

  const [deleteTargetRows, setDeleteTargetRows] = useState<number[]>([]);

  // permissions
  function getToolTipText(rt: typeof selectedReportType) {
    if (!rt) return '';
    if (usersTier.rank <= gPanelTiers.starter.rank && !rt.includeInStarter) {
      return Messages.Errors.Tier.Acting(gPanelTiers.standard.displayValue);
    }
    if (
      !isAbleToSeeUIElement(toolsRoles, ToolsRoles.MaunalRunReport.value) &&
      !isAbleToSeeUIElement(toolsRoles, rt.role)
    ) {
      const matchingRole = Object.keys(ToolsRoles).find(
        (k) => ToolsRoles[k].value === rt.role
      );
      return matchingRole
        ? Messages.Errors.Permission.Acting(ToolsRoles[matchingRole].name)
        : Messages.Errors.Permission.Acting(rt.role);
    }
    return rt.description;
  }

  const canEdit = isAbleToSeeUIElement(toolsRoles, ToolsRoles.EditReport.value);
  const runTooltipText = getToolTipText(selectedReportType);
  const canRun =
    !reportTypesLoading && runTooltipText === selectedReportType?.description;
  const canDelete = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.DeleteReport.value
  );

  const runPermissionBlockedReason =
    reportTypesLoading || canRun ? undefined : runTooltipText || undefined;
  const runDisabledReason = isRunning
    ? 'Report is running'
    : selectedReport?.manualRun === false
    ? 'This report is scheduled'
    : runPermissionBlockedReason;
  const editDisabledReason = canEdit
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.EditReport.name);
  const deleteDisabledReason = canDelete
    ? undefined
    : Messages.Errors.Permission.Acting(ToolsRoles.DeleteReport.name);

  const actionsMenu: SimpleDrawerAction[] | undefined = selectedReport
    ? [
        {
          label: 'Run',
          icon: <PlayArrow fontSize="small" />,
          onClick: () => {
            if (selectedReport.status !== 'RUNNING') {
              handleStartReport(selectedReport);
            }
          },
          disabled:
            (!canRun || selectedReport.manualRun === false || isRunning) &&
            selectedReport.status !== 'RUNNING',
          disabledReason: runDisabledReason,
        },
        {
          label: 'Edit',
          icon: <Edit fontSize="small" />,
          onClick: () => {
            handleClose();
            handleEditReport(selectedReport.id);
          },
          disabled: !canEdit,
          disabledReason: editDisabledReason,
        },
        {
          label: 'Delete',
          icon: <Delete fontSize="small" />,
          onClick: () => {
            setDeleteTargetRows([selectedReport.id]);
            setOpenDeleteDialog(true);
          },
          disabled: !canDelete,
          disabledReason: deleteDisabledReason,
        },
      ]
    : undefined;

  // render
  return (
    <>
      <SimpleDrawer
        title="Report Details"
        open={!!selectedReportId}
        onClose={handleClose}
        isLoading={isLoading}
        actionsMenu={actionsMenu}
      >
        {selectedReport ? (
          <DrawerContent
            selectedReport={selectedReport}
            reportTypes={removedHiddenReports}
            selectedSharedDrives={selectedSharedDrives}
            calendarEmailToNameMap={calendarEmailToNameMap}
          />
        ) : (
          !isLoading && (
            <Typography sx={{ p: 3 }} color="error">
              Report not found.
            </Typography>
          )
        )}
      </SimpleDrawer>

      {openDeleteDialog && deleteTargetRows.length > 0 && (
        <ReportsDeleteDialog
          open={openDeleteDialog}
          handleClose={() => setOpenDeleteDialog(false)}
          selectedRows={deleteTargetRows}
          setSelectedRows={setDeleteTargetRows}
          changeLogRoles={changeLogRoles}
        />
      )}

      {openRunConfirmDialog && reportForRun && (
        <ReportsRunDialog
          open={openRunConfirmDialog}
          onClose={closeRunConfirmDialog}
          reportName={reportForRun.reportName}
          isRunning={isRunning}
          onConfirm={handleConfirmRunDialog}
        />
      )}

      {openChangeLogDialog && reportForRun && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={closeChangeLogDialog}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={handleConfirmRun}
          mutateLoading={isRunning}
        />
      )}
    </>
  );
}
